<script setup lang="ts">
import { atLeast8CharsWithOneUppercaseOneLowercaseOneNumberAndOneSpecialCharacter } from "@zirp/util";
import { useEmailSignup } from "~/composables/auth/useEmailSignup";
import { useFetchProfile } from "~/composables/auth/useFetchProfile";
import { useGithubLogin } from "~/composables/auth/useGithubLogin";
import { useGoogleLogin } from "~/composables/auth/useGoogleLogin";

definePageMeta({
  layout: "auth",
});

useSeoMeta({
  title: "Sign up",
});

const { user, loading: userLoading } = useFetchProfile();
const { githubLogin, loading } = useGithubLogin();
const { googleLogin, loading: googleLoading } = useGoogleLogin();
const { emailSignup, loading: emailLoading, error: emailError } = useEmailSignup();
const toast = useToast();

const fields = [
  {
    name: "email",
    type: "email",
    label: "Email",
    placeholder: "Enter your email",
  },
  {
    name: "password",
    label: "Password",
    type: "password",
    placeholder: "Enter your password",
  },
];
// const fields = [];

const validate = (state: any) => {
  const errors = [];
  if (!state.email) errors.push({ path: "email", message: "Email is required" });

  if (!state.password) errors.push({ path: "password", message: "Password is required" });
  if (!atLeast8CharsWithOneUppercaseOneLowercaseOneNumberAndOneSpecialCharacter.test(state.password)) {
    errors.push({ path: "password", message: "At least 8 charaters long, with at least one upper-case, one lower-cases, one number and one special character" })
  }
  return errors;
};

const providers = ref([
{
    label: "Sign up with Google",
    icon: "i-devicon-google",
    color: "black" as const,
    loading: googleLoading.value,
    click: () => {
      googleLogin();
    },
  },
  {
    label: "Sign up with GitHub",
    icon: "i-simple-icons-github",
    color: "black" as const,
    loading: loading.value,
    click: () => {
      githubLogin();
    },
  },
]);

async function onSubmit(data: { email: string, password: string }) {
  await emailSignup(data.email, data.password);
  if (emailError.value) {
    toast.add({
      title: "Error signing you up",
      color: "red",
    })
  }
}

watch(() => user.value?.id, () => {
  if (!user.value?.id) return;
  navigateTo("/onboarding");
});

onMounted(() => {
  if (!user.value?.id) return;
  navigateTo("/onboarding");
})

</script>

<!-- eslint-disable vue/multiline-html-element-content-newline -->
<!-- eslint-disable vue/singleline-html-element-content-newline -->
<template>
  <UCard class="max-w-sm w-full bg-white/75 dark:bg-white/5 backdrop-blur" v-if="!userLoading && !user?.id">
    <UAuthForm :fields="fields" :validate="validate" :providers="providers" align="bottom" title="Create an account"
      :ui="{ base: 'text-center', footer: 'text-center' }"
      :submit-button="{ label: 'Create account', loading: emailLoading }" @submit="onSubmit">
      <template #description>
        Already have an account? <NuxtLink to="/login" class="text-primary font-medium">Login</NuxtLink>.
      </template>

      <template #footer>
        By signing up, you agree to our <NuxtLink to="/" class="text-primary font-medium">Privacy Policy</NuxtLink> and
        <NuxtLink to="/" class="text-primary font-medium">Terms of Service</NuxtLink>.
      </template>
    </UAuthForm>
  </UCard>
</template>
